import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { guardPhrase, guardSees, type CorridorParams, type Direction4 } from '@il-colpo/engine';
import { Button, Icon } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface Cell {
  x: number;
  y: number;
}
interface Guard extends Cell {
  facing: Direction4;
}
interface CorridorView {
  w: number;
  h: number;
  walls: Cell[];
  shadows: Cell[];
  exit: Cell;
  round: number;
  tokens: { player: string; x: number; y: number; committed: boolean }[];
  guards: Guard[];
  vision: number;
  radar?: { routes: Cell[][]; next: Cell[][] };
  gadgets?: { coins: number; petards: number };
}

const CELL = 46;
const HEARD = ['corGuard', 'corHold', 'corShadow'] as const;
const ARROWS: Record<Direction4, string> = { n: '↑', e: '→', s: '↓', w: '←' };
const KEYS: Record<string, Direction4> = {
  ArrowUp: 'n',
  ArrowRight: 'e',
  ArrowDown: 's',
  ArrowLeft: 'w',
};
const VEC: Record<Direction4, Cell> = {
  n: { x: 0, y: -1 },
  e: { x: 1, y: 0 },
  s: { x: 0, y: 1 },
  w: { x: -1, y: 0 },
};

/** Guarded corridor (GDD §8.7): everybody moves a token, then the guards step. */
export function CorridorPanel({ puzzleId, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { view, act } = useGame();
  const v = raw as unknown as CorridorView;
  const [gadget, setGadget] = useState<'coin' | 'petard' | null>(null);
  const me = v.tokens.find((tk) => tk.player === view.you.id);
  const canMove = me !== undefined && !me.committed && !(me.x === v.exit.x && me.y === v.exit.y);

  const par = {
    w: v.w,
    h: v.h,
    walls: v.walls,
    shadows: v.shadows,
    exit: v.exit,
    starts: [],
    routes: [],
    vision: v.vision,
  } as CorridorParams;
  const walkable = (c: Cell) =>
    c.x >= 0 &&
    c.y >= 0 &&
    c.x < v.w &&
    c.y < v.h &&
    !v.walls.some((w) => w.x === c.x && w.y === c.y);
  const watched = new Set<string>();
  for (const g of v.guards) {
    for (let y = 0; y < v.h; y++) {
      for (let x = 0; x < v.w; x++)
        if (guardSees(par, g, { x, y }, v.vision)) watched.add(`${x},${y}`);
    }
  }

  const move = (dir: Direction4 | 'stay') => act(puzzleId, { kind: 'corridor.move', dir });

  useEffect(() => {
    if (!canMove) return;
    const onKey = (e: KeyboardEvent) => {
      const dir = KEYS[e.key];
      if (
        dir &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLSelectElement)
      ) {
        e.preventDefault();
        act(puzzleId, { kind: 'corridor.move', dir });
      } else if (e.key === ' ' && e.target === document.body) {
        e.preventDefault();
        act(puzzleId, { kind: 'corridor.move', dir: 'stay' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canMove, act, puzzleId]);

  const tap = (x: number, y: number) => {
    if (!gadget || !walkable({ x, y })) return;
    act(puzzleId, { kind: gadget === 'coin' ? 'corridor.coin' : 'corridor.petard', x, y });
    setGadget(null);
  };

  return (
    <div className={styles['stack']}>
      <p className={styles['progress']}>{t('puzzle.corridor.round', { n: v.round + 1 })}</p>
      <svg
        viewBox={`0 0 ${v.w * CELL} ${v.h * CELL}`}
        className={styles['board']}
        role="group"
        aria-label={t('puzzle.corridor.board')}
      >
        {Array.from({ length: v.w * v.h }, (_, i) => {
          const x = i % v.w;
          const y = Math.floor(i / v.w);
          const wall = v.walls.some((w) => w.x === x && w.y === y);
          const shadow = v.shadows.some((s) => s.x === x && s.y === y);
          const seen = watched.has(`${x},${y}`);
          return (
            <g key={i} onClick={() => tap(x, y)}>
              <rect
                className={[
                  styles['cell'],
                  wall ? styles['wallCell'] : '',
                  shadow ? styles['shadow'] : '',
                  seen ? styles['seen'] : '',
                  gadget && !wall ? styles['aim'] : '',
                ].join(' ')}
                x={x * CELL}
                y={y * CELL}
                width={CELL}
                height={CELL}
              />
            </g>
          );
        })}
        {v.radar
          ? v.radar.routes.map((route, gi) => (
              <polyline
                key={gi}
                className={styles['route']}
                points={route
                  .map((c) => `${c.x * CELL + CELL / 2},${c.y * CELL + CELL / 2}`)
                  .join(' ')}
              />
            ))
          : null}
        <g
          transform={`translate(${v.exit.x * CELL},${v.exit.y * CELL})`}
          className={styles['exit']}
        >
          <rect x={6} y={6} width={CELL - 12} height={CELL - 12} rx={6} />
          <text x={CELL / 2} y={CELL / 2 + 6} textAnchor="middle">
            {'⇧'}
          </text>
        </g>
        {v.guards.map((g, i) => {
          const d = VEC[g.facing];
          return (
            <g
              key={i}
              transform={`translate(${g.x * CELL + CELL / 2},${g.y * CELL + CELL / 2})`}
              className={styles['guard']}
            >
              <circle r={14} />
              <polygon
                points="0,-8 7,5 -7,5"
                transform={`rotate(${Math.atan2(d.y, d.x) * (180 / Math.PI) + 90})`}
              />
              <text y={30} textAnchor="middle">
                {i + 1}
              </text>
            </g>
          );
        })}
        {v.tokens.map((tk) => {
          const mine = tk.player === view.you.id;
          const who = view.players.find((p) => p.id === tk.player);
          return (
            <g
              key={tk.player}
              transform={`translate(${tk.x * CELL + CELL / 2},${tk.y * CELL + CELL / 2})`}
              className={[
                styles['token'],
                mine ? styles['mine'] : '',
                tk.committed ? styles['committed'] : '',
              ].join(' ')}
            >
              <circle r={mine ? 12 : 10} />
              <text textAnchor="middle" y={4}>
                {(who?.name ?? '?').charAt(0)}
              </text>
            </g>
          );
        })}
      </svg>

      {me ? (
        <div className={styles['dpad']} role="group" aria-label={t('puzzle.corridor.move')}>
          <span />
          <Button
            size="md"
            variant="secondary"
            disabled={!canMove}
            aria-label={t('puzzle.corridor.north')}
            onClick={() => move('n')}
          >
            {ARROWS.n}
          </Button>
          <span />
          <Button
            size="md"
            variant="secondary"
            disabled={!canMove}
            aria-label={t('puzzle.corridor.west')}
            onClick={() => move('w')}
          >
            {ARROWS.w}
          </Button>
          <Button
            size="md"
            variant="ghost"
            disabled={!canMove}
            aria-label={t('puzzle.corridor.stay')}
            onClick={() => move('stay')}
          >
            <Icon name="pause" />
          </Button>
          <Button
            size="md"
            variant="secondary"
            disabled={!canMove}
            aria-label={t('puzzle.corridor.east')}
            onClick={() => move('e')}
          >
            {ARROWS.e}
          </Button>
          <span />
          <Button
            size="md"
            variant="secondary"
            disabled={!canMove}
            aria-label={t('puzzle.corridor.south')}
            onClick={() => move('s')}
          >
            {ARROWS.s}
          </Button>
          <span />
        </div>
      ) : null}
      {me?.committed ? <p className={styles['status']}>{t('puzzle.corridor.waiting')}</p> : null}

      {v.gadgets ? (
        <div className={styles['gadgets']}>
          <Button
            variant="secondary"
            size="md"
            disabled={v.gadgets.coins <= 0}
            selected={gadget === 'coin'}
            onClick={() => setGadget(gadget === 'coin' ? null : 'coin')}
          >
            {t('puzzle.corridor.coin', { count: v.gadgets.coins })}
          </Button>
          <Button
            variant="secondary"
            size="md"
            disabled={v.gadgets.petards <= 0}
            selected={gadget === 'petard'}
            onClick={() => setGadget(gadget === 'petard' ? null : 'petard')}
          >
            {t('puzzle.corridor.petard', { count: v.gadgets.petards })}
          </Button>
          {gadget ? <span className={styles['muted']}>{t('puzzle.corridor.aim')}</span> : null}
        </div>
      ) : null}

      {v.radar ? (
        <>
          <p className={styles['muted']}>{t('puzzle.corridor.radarHint')}</p>
          <ul className={styles['clues']}>
            {v.guards.map((g, i) => {
              const next = v.radar?.next[i]?.[0];
              if (!next) return null;
              const dir = (['n', 'e', 's', 'w'] as const).find(
                (d) => VEC[d].x === Math.sign(next.x - g.x) && VEC[d].y === Math.sign(next.y - g.y),
              );
              return dir ? <ClueLine key={i} phrase={guardPhrase(i, dir)} /> : null;
            })}
          </ul>
        </>
      ) : (
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      )}
    </div>
  );
}
